/**
 * izyRent Quantity Step Interceptor
 * 
 * Intercepts quantity increment/decrement buttons in the izyRent booking app and applies
 * custom step values and minimum quantity constraints from product metafields.
 * 
 * @description
 * This script reads configuration from a JSON script tag injected by the Liquid template
 * and overrides the default quantity increment/decrement behavior. It ensures:
 * - Custom step values (e.g., increment by 2 instead of 1)
 * - Minimum quantity enforcement (e.g., cannot go below 4)
 * - Proper integration with izyRent's price calculation system
 * 
 * @configuration
 * Configuration is provided via product metafields:
 * - custom.rental_quantity_step: Step value for increment/decrement (default: 1)
 * - custom.rental_minimun_quantity: Minimum allowed quantity (default: 1)
 * 
 * These values are injected into the page via a JSON script tag with id="izyrent-config"
 * by the izyrent-custom.liquid snippet.
 * 
 * @integration
 * The script listens for izyRent app events:
 * - izyrentReady: Fired when the app initializes
 * - izyrentCheckDate: Fired after quantity/date updates
 * 
 * @usage
 * Include the izyrent-custom.liquid snippet in your product template where izyRent is used.
 * The snippet will conditionally load this script if min_qty > 0 or qty_step > 0.
 */

(function () {
    'use strict';

    // ====== Configuration ======
    const DEFAULT_STEP = 1;
    const DEFAULT_MIN = 1;

    let config = {
        incrementStep: DEFAULT_STEP,
        decrementStep: DEFAULT_STEP,
        minQty: DEFAULT_MIN
    };

    let cachedMaxStock = Infinity;

    // ====== Config Loading ======
    const loadConfig = () => {
        const scriptTag = document.querySelector('script#izyrent-config[type="application/json"]');

        if (!scriptTag) {
            console.warn('[izyRent Qty] Config script tag not found, using defaults');
            return;
        }

        try {
            const data = JSON.parse(scriptTag.textContent);

            if (data?.izyrent) {
                config.incrementStep = parseInt(data.izyrent.qty_step, 10) || DEFAULT_STEP;
                config.decrementStep = parseInt(data.izyrent.qty_step, 10) || DEFAULT_STEP;
                config.minQty = parseInt(data.izyrent.min_qty, 10) || DEFAULT_MIN;
            }
        } catch (error) {
            console.error('[izyRent Qty] Failed to parse config', error);
        }
    };

    // ====== Stock Management ======
    const updateMaxStock = () => {
        const el = document.querySelector('.maxStock');
        if (el) {
            const match = el.textContent?.match(/Stock:\s*(\d+)/);
            if (match && match[1]) {
                cachedMaxStock = parseInt(match[1], 10);
            }
        }
    };

    // ====== Input Constraints ======
    const applyMinQty = () => {
        const input = document.querySelector('.izyMultiInput');
        if (!input || !(input instanceof HTMLInputElement)) return;

        input.type = 'number';
        input.min = config.minQty.toString();
        input.step = config.incrementStep.toString();

        const current = parseInt(input.value || '0', 10);
        if (current < config.minQty) {
            input.value = config.minQty.toString();
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };

    // ====== Event Handlers ======
    /**
     * @param {Event} e
     */
    const handleInput = (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement) || !input.classList.contains('izyMultiInput')) return;

        const value = parseInt(input.value || '0', 10);

        if (value < config.minQty) {
            input.value = config.minQty.toString();
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };

    /**
     * @param {Event} e
     */
    const handleClick = (e) => {
        const target = e.target;
        if (!target || !(target instanceof Element)) return;

        const addBtn = target.closest('.addizy');
        const subBtn = target.closest('.subizy');

        if (!addBtn && !subBtn) return;

        e.stopImmediatePropagation();
        e.preventDefault();

        const input = document.querySelector('.izyMultiInput');
        if (!input || !(input instanceof HTMLInputElement)) return;

        const current = parseInt(input.value || config.minQty.toString(), 10);
        let newQty;

        if (addBtn) {
            updateMaxStock();
            newQty = Math.min(current + config.incrementStep, cachedMaxStock);
        } else {
            newQty = Math.max(current - config.decrementStep, config.minQty);
        }

        if (newQty !== current) {
            input.value = newQty.toString();
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (current < config.minQty) {
            input.value = config.minQty.toString();
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };

    // ====== Initialization ======
    const init = () => {
        loadConfig();
        updateMaxStock();
        applyMinQty();

        document.removeEventListener('click', handleClick, true);
        document.removeEventListener('input', handleInput, true);

        document.addEventListener('click', handleClick, true);
        document.addEventListener('input', handleInput, true);
    };

    // ====== Setup ======
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    document.addEventListener('izyrentReady', () => {
        init();
    });

    document.addEventListener('izyrentCheckDate', () => {
        setTimeout(() => {
            updateMaxStock();
            applyMinQty();
            document.removeEventListener('click', handleClick, true);
            document.removeEventListener('input', handleInput, true);
            document.addEventListener('click', handleClick, true);
            document.addEventListener('input', handleInput, true);
        }, 150);
    });

    setTimeout(() => {
        updateMaxStock();
        applyMinQty();
    }, 500);
})();
